import { useEffect, useRef, useState, type FormEvent } from 'react';
import { bff } from '../../../lib/bff';
import { useApp } from '../../../lib/app-context';
import './support-cases.css';

interface CaseSummary {
  id: string;
  subject: string;
  priority: 'normal' | 'high';
  status: 'open' | 'in_progress' | 'resolved';
  createdAt: string;
}

export function SupportCases() {
  const { t } = useApp();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<'normal' | 'high'>('normal');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [cases, setCases] = useState<CaseSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const requestKey = useRef<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const list = await bff<CaseSummary[]>('/v1/ai/support/cases');
      setCases(list);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void refresh(); }, []);

  function resetKey() {
    requestKey.current = null;
    setCreatedId(null);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || !consent || subject.trim().length < 5 || description.trim().length < 15) return;
    setBusy(true);
    setError(false);
    if (!requestKey.current) requestKey.current = crypto.randomUUID();
    try {
      const answer = await bff<CaseSummary>('/v1/ai/support/cases', {
        method: 'POST',
        headers: { 'idempotency-key': requestKey.current },
        body: JSON.stringify({
          subject: subject.trim(),
          description: description.trim(),
          priority,
          consentToShareDescription: true,
        }),
      });
      setCreatedId(answer.id);
      setSubject('');
      setDescription('');
      setPriority('normal');
      setConsent(false);
      requestKey.current = null;
      await refresh();
    } catch {
      // Keep the same idempotency key for a safe retry of an interrupted submission.
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="qm-support-cases" aria-labelledby="qm-support-cases-title">
      <h2 id="qm-support-cases-title">{t('shell.supportCases.title')}</h2>
      <p>{t('shell.supportCases.intro')}</p>
      <form onSubmit={(event) => void submit(event)}>
        <div className="qm-support-cases__row">
          <label htmlFor="qm-case-subject">{t('shell.supportCases.subject')}</label>
          <input id="qm-case-subject" required minLength={5} maxLength={120} value={subject}
            onChange={(e) => { setSubject(e.target.value); resetKey(); }} disabled={busy} />
        </div>
        <div className="qm-support-cases__row">
          <label htmlFor="qm-case-priority">{t('shell.supportCases.priority')}</label>
          <select id="qm-case-priority" value={priority}
            onChange={(e) => { setPriority(e.target.value as 'normal' | 'high'); resetKey(); }} disabled={busy}>
            <option value="normal">{t('shell.supportCases.normal')}</option>
            <option value="high">{t('shell.supportCases.high')}</option>
          </select>
        </div>
        <div className="qm-support-cases__row">
          <label htmlFor="qm-case-description">{t('shell.supportCases.description')}</label>
          <textarea id="qm-case-description" required minLength={15} maxLength={2000}
            rows={5} value={description} onChange={(e) => { setDescription(e.target.value); resetKey(); }}
            disabled={busy} />
        </div>
        <p className="qm-support-cases__caution">{t('shell.supportCases.caution')}</p>
        <label className="qm-support-cases__consent">
          <input type="checkbox" checked={consent} disabled={busy} onChange={(e) => setConsent(e.target.checked)} />
          <span>{t('shell.supportCases.consent')}</span>
        </label>
        <button type="submit" disabled={busy || !consent || subject.trim().length < 5 || description.trim().length < 15}>
          {busy ? t('shell.supportCases.sending') : t('shell.supportCases.send')}
        </button>
        {createdId && <p role="status">{t('shell.supportCases.saved')} <strong>{createdId}</strong></p>}
        {error && <p role="alert">{t('shell.supportCases.failure')}</p>}
      </form>
      <div className="qm-support-cases__history">
        <div className="qm-support-cases__history-head">
          <h3>{t('shell.supportCases.history')}</h3>
          <button type="button" onClick={() => void refresh()} disabled={loading}>
            {loading ? t('shell.supportCases.loading') : t('shell.supportCases.refresh')}
          </button>
        </div>
        {cases.length === 0 ? <p>{t('shell.supportCases.empty')}</p> :
          <ul>{cases.map((item) => <li key={item.id}>
            <strong>{item.subject}</strong><small>{item.id}</small>
            <span>{item.status === 'open' ? t('shell.supportCases.status.open') : item.status === 'in_progress' ? t('shell.supportCases.status.in_progress') : t('shell.supportCases.status.resolved')}</span>
          </li>)}</ul>}
      </div>
    </section>
  );
}
